/** URL locale segment: English uses no prefix (`/`); others use `/ko`, `/es`, `/zh`. */
export type AppLocale = 'en' | 'ko' | 'es' | 'zh';

const PREFIX_LOCALES = ['ko', 'es', 'zh'] as const satisfies readonly AppLocale[];

export function localeFromPathname(pathname: string): AppLocale {
  for (const loc of PREFIX_LOCALES) {
    const p = `/${loc}`;
    if (pathname === p || pathname.startsWith(`${p}/`)) return loc;
  }
  return 'en';
}

/** Path without leading locale prefix; always starts with `/`. */
export function pathWithoutLocale(pathname: string): string {
  for (const loc of PREFIX_LOCALES) {
    const p = `/${loc}`;
    if (pathname === p) return '/';
    if (pathname.startsWith(`${p}/`)) {
      const rest = pathname.slice(p.length);
      return rest.startsWith('/') ? rest : `/${rest}`;
    }
  }
  return pathname || '/';
}

/** Build absolute app path for a locale. `rest` is like `/faq` or `/`.
 *  Always returns trailing-slash paths so <Link> hrefs match Cloudflare Pages
 *  served URLs and avoid 301 redirects that Google marks as "page with redirect".
 */
export function pathWithLocale(rest: string, locale: AppLocale): string {
  const r = rest === '' || rest === '/' ? '/' : rest.startsWith('/') ? rest : `/${rest}`;
  // Add trailing slash to all non-root paths (idempotent if already present)
  const slash = (p: string) => (p === '/' || p.endsWith('/') ? p : `${p}/`);
  if (locale === 'en') return slash(r);
  const prefix = `/${locale}`;
  if (r === '/') return `${prefix}/`;
  return `${prefix}${slash(r)}`;
}

/** BCP 47 tag for `lang` / schema.org */
export function htmlLangTag(locale: AppLocale): string {
  if (locale === 'zh') return 'zh-Hans';
  return locale;
}

/** Host-only path for share/copy footers (no https://). */
export function footerDomainBase(locale: AppLocale): string {
  return locale === 'en' ? 'lotto-number-generator.com' : `lotto-number-generator.com/${locale}`;
}

export function footerDomainAutoGenerate(locale: AppLocale): string {
  return locale === 'en' ? 'lotto-number-generator.com/#auto-search' : `lotto-number-generator.com/${locale}#auto-search`;
}
